import React from "react";
import {
  Box,
  Breadcrumbs,
  Fab,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, ChevronRight, HomeOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CREATE_NOTICE } from "../../routes";
import {
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../../styles/managementShared";
import { useMobileFabVisibility } from "../../hooks/useMobileFabVisibility";

export type CreateCrumb = { label: string; to: string };

type CreateSectionHeaderProps = {
  /** Pages between Create and this one, e.g. Certificates › Templates. */
  crumbs: CreateCrumb[];
  /** The last breadcrumb. Defaults to the title. */
  current?: string;
  title: string;
  subtitle?: React.ReactNode;
  /** Sits beside the title, e.g. a status chip. */
  badge?: React.ReactNode;
  /** Buttons on the right of the title, e.g. Save draft. */
  actions?: React.ReactNode;
  /** The first crumb after Home. Defaults to Create; Planners use Calendar. */
  root?: CreateCrumb;
};

/**
 * Breadcrumbs, heading and subtitle for a page reached from Create notice, such
 * as Certificates or Signatures, styled like the Library worksheet pages. On
 * phones a floating back button goes up one level.
 */
const CreateSectionHeader: React.FC<CreateSectionHeaderProps> = ({
  crumbs,
  current,
  title,
  subtitle,
  badge,
  actions,
  root = { label: "Create", to: CREATE_NOTICE },
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileFabVisible = useMobileFabVisibility(isMobile);
  const trail: CreateCrumb[] = [root, ...crumbs];
  const parent = trail[trail.length - 1];

  return (
    <>
      <Box sx={{ mt: 1, mb: 2 }}>
        <Breadcrumbs
          separator={<ChevronRight sx={{ fontSize: 18, color: "text.secondary" }} />}
          aria-label="breadcrumb"
        >
          <Link
            component="button"
            underline="hover"
            color="text.secondary"
            aria-label="Home"
            onClick={() => navigate("/")}
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <HomeOutlined sx={{ fontSize: 22 }} />
          </Link>
          {trail.map((crumb) => (
            <Link
              key={crumb.to}
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(crumb.to)}
            >
              {crumb.label}
            </Link>
          ))}
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>
            {current ?? title}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* On phones the actions go under the title, so the title and subtitle keep the full width. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                fontWeight: 500,
                color: "text.primary",
                mb: 0,
              }}
            >
              {title}
            </Typography>
            {badge}
          </Box>
          {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
        </Box>
        {actions && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, flexShrink: 0 }}>{actions}</Box>
        )}
      </Box>

      {isMobile && (
        <Fab
          color="primary"
          aria-label={`Back to ${parent.label}`}
          onClick={() => navigate(parent.to)}
          sx={mobileFabWithScrollVisibility(MANAGEMENT_MOBILE_FAB_SX, mobileFabVisible)}
        >
          <ArrowBack sx={{ fontSize: 28 }} />
        </Fab>
      )}
    </>
  );
};

export default CreateSectionHeader;
